import {v4 as uuidv4} from 'uuid';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import {useMemo, useState} from 'react';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';

import {LoadingButton} from "@mui/lab";
import {
  Box,
  Card,
  Grid,
  Stack,
  Button,
  Divider,
  Container,
  TextField, Typography, Autocomplete
} from '@mui/material';

import axios from "src/utils/axios";

import {useTranslate} from 'src/locales';

import Iconify from "src/components/iconify";
import {
  RHFTextField,
} from 'src/components/hook-form';

// ----------------------------------------------------------------------

IngredientsForm.propTypes = {
  isEdit: PropTypes.bool,
  currentRecipe: PropTypes.object,
  onSubmit: PropTypes.func
};

export default function IngredientsForm({isEdit, currentRecipe, onSubmit}) {
  const {
    control,
    formState: {isSubmitting}
  } = useFormContext();

  const {t} = useTranslate();

  const [ingredientList, setIngredientList] = useState({});
  const [equipmentList, setEquipmentList] = useState({});

  const {fields: groups, append: appendGroup, remove: removeGroup} = useFieldArray({
    control,
    name: 'groups',
  });

  const {fields: ingredients, append: appendIngredient, remove: removeIngredient} = useFieldArray({
    control,
    name: 'ingredients',
  });

  const {fields: appliances, append: appendAppliance, remove: removeAppliance} = useFieldArray({
    control,
    name: 'appliances',
  });

  const handleAddGroup = (key) => {
    const addedIngredientKey = uuidv4();
    appendGroup({
      key,
      name: ''
    });
    handleAddIngredient(addedIngredientKey, key);
  };

  const handleAddIngredient = (key, group) => {
    appendIngredient({
      key,
      group,
      selected: null,
      quantity: 1,
    });
  };

  const handleAddAppliance = (key) => {
    appendAppliance({});
  };

  const handleRemoveGroupByKey = (key) => {
    const groupIndex = groups.findIndex(group => group.key === key);
    if (groupIndex !== -1) {
      handleRemoveGroup(groupIndex);
    }
  };

  const handleRemoveGroup = (index) => {
    const removedGroupKey = groups[index].key;
    removeGroup(index);

    const removeIngredientKeys = ingredients.filter(ingredient => ingredient.group === removedGroupKey).map((ingredient) => ingredient.key);
    handleRemoveIngredientByKey(removeIngredientKeys);
  };

  const handleRemoveIngredient = (index) => {
    removeIngredient(index);
  };

  const handleRemoveAppliance = (index) => {
    removeAppliance(index);
  };

  const handleRemoveIngredientByKey = (key) => {
    if (Array.isArray(key)) {
      const indexesToRemove = [];
      key.forEach((ingredientKeyToRemove) => {
        const ingredientIndex = ingredients.findIndex(ingredient => ingredient.key === ingredientKeyToRemove);
        if (ingredientIndex !== -1) {
          indexesToRemove.unshift(ingredientIndex);
        }
      });

      if (indexesToRemove.length > 0) {
        handleRemoveIngredient(indexesToRemove);
      }
    } else {
      const ingredientIndex = ingredients.findIndex(ingredient => ingredient.key === key);
      if (ingredientIndex !== -1) {
        handleRemoveIngredient(ingredientIndex);
      }
    }
  };

  const searchIngredients = async (event, newValue, ingredientIndex) => {
    const listResponse = await axios.get(`api/ingredient/search/${newValue}`);

    const preparedList = listResponse.data.list.map((ingredientObject) => ({
      value: ingredientObject.id,
      label: ingredientObject.name,
      allowedUnits: ingredientObject.allowedUnits
    }));

    setIngredientList(prevState => ({...prevState, [ingredientIndex]: preparedList}));
  }

  const onIngredientNameInputChange = (event, newValue, ingredientIndex) => {
    if (newValue.length > 0) {
      throttledIngredientNameInputChange(event, newValue, ingredientIndex)
    } else {
      setIngredientList(prevState => ({...prevState, [ingredientIndex]: []}));
    }
  };

  const throttledIngredientNameInputChange = useMemo(
    () => throttle(searchIngredients, 300)
    , []
  );

  const searchEquipment = async (event, newValue, applianceIndex) => {
    const listResponse = await axios.get(`api/equipment/search/${newValue}`);

    const preparedList = listResponse.data.list.map((equipmentObject) => ({
      value: equipmentObject.id,
      label: equipmentObject.name
    }));

    setEquipmentList(prevState => ({...prevState, [applianceIndex]: preparedList}));
  }

  const onEquipmentNameInputChange = (event, newValue, applianceIndex) => {
    if (newValue.length > 0) {
      throttledEquipmentNameInputChange(event, newValue, applianceIndex)
    } else {
      setEquipmentList(prevState => ({...prevState, [applianceIndex]: []}));
    }
  };

  const throttledEquipmentNameInputChange = useMemo(
    () => throttle(searchEquipment, 300)
    , []
  );

  return (
    <Container disableGutters>
      <Stack spacing={3}>
        <Typography variant="h3">
          {t('page.recipe.add.form.ingredients.title')}
        </Typography>
        {groups.map((group, index) => (
          <Card sx={{p: 3}} key={group.key}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Stack spacing={3} direction="row" sx={{mb: 3}}>
                  <Typography variant="h6" sx={{color: 'text.disabled'}}>
                    {`${t('page.recipe.add.form.ingredients.group')} ${index + 1}`}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Iconify icon="eva:trash-2-outline"/>}
                    onClick={() => handleRemoveGroupByKey(group.key)}
                  >
                    {t('page.recipe.add.form.ingredients.remove')}
                  </Button>
                </Stack>
                <RHFTextField
                  size="small"
                  name={`groups[${index}].name`}
                  label={t('page.recipe.add.form.ingredients.group_name')}
                  InputLabelProps={{shrink: true}}
                />
                <Typography variant="body2" sx={{mt: 1}}>
                  {t('page.recipe.add.form.ingredients.group_description')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" sx={{color: 'text.disabled', mb: 3}}>
                  {t('page.recipe.add.form.ingredients.ingredients_header')}
                </Typography>
                <Stack spacing={3} divider={<Divider flexItem sx={{borderStyle: 'dashed'}}/>}>
                  {ingredients.map((ingredient, ingredientIndex) =>
                    (ingredient.group === group.key) ? (
                      <Stack spacing={2} key={ingredient.key}>
                        <Stack direction="row" spacing={2}>
                          <Controller
                            name={`ingredients[${ingredientIndex}].selected`}
                            control={control}
                            render={({field, fieldState}) => (
                              <Autocomplete
                                {...field}
                                size="small"
                                name={`ingredients[${ingredientIndex}].selected`}
                                label="Name"
                                fullWidth
                                freeSolo
                                openOnFocus
                                autoHighlight
                                autoSelect
                                getOptionDisabled={option => !option.value}
                                onInputChange={(event, newValue) => onIngredientNameInputChange(event, newValue, ingredientIndex)}
                                options={ingredientList[ingredientIndex] || []}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.value}>
                                    {option.label}
                                  </li>
                                )}
                                renderInput={(params) =>
                                  <TextField
                                    {...params}
                                    label={t('page.recipe.add.form.ingredients.ingredient_required')}
                                    variant="outlined"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                  />
                                }
                                onChange={(event, newValue) => {
                                  if (typeof newValue !== 'object') {
                                    return;
                                  }

                                  if (newValue?.addedOption) {
                                    newValue.label = newValue.value;
                                  }
                                  field.onChange(newValue);
                                }}
                                filterOptions={(options, params) => {
                                  const {inputValue} = params;
                                  const isExisting = options.some((option) => inputValue === option.title);
                                  if (!isExisting) {
                                    if (inputValue !== '') {
                                      options.push({
                                        value: inputValue,
                                        label: `Dodaj składnik "${inputValue}"`,
                                        addedOption: true
                                      });
                                    } else {
                                      options.push({
                                        value: '',
                                        label: `Wpisz nazwę składnika, aby wyszukać`
                                      });
                                    }
                                  }

                                  return options;
                                }}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                              />
                            )}
                          />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                          <RHFTextField
                            size="small"
                            name={`ingredients[${ingredientIndex}].quantity`}
                            label= {t('page.recipe.add.form.ingredients.quantity')}
                            required
                            InputLabelProps={{shrink: true}}
                          />
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Iconify icon="eva:trash-2-outline"/>}
                            onClick={() => handleRemoveIngredientByKey(ingredient.key)}
                            sx={{
                              padding: {xs: 0, sm: 'initial'},
                              minWidth: {xs: 0, sm: 'initial'},
                              paddingLeft: {xs: '10px', sm: 'initial'}
                            }}
                          >
                            <Typography variant="button" sx={{display: {xs: 'none', sm: 'inline'}}}>{t('page.recipe.add.form.ingredients.remove')}</Typography>
                          </Button>
                        </Stack>
                      </Stack>
                    ) : '')}
                </Stack>
                <Button size="small" startIcon={<Iconify icon="eva:plus-fill"/>}
                        onClick={() => handleAddIngredient(uuidv4(), group.key)}
                        sx={{flexShrink: 0, mt: 2}}>
                  {t('page.recipe.add.form.ingredients.add_new_ingredient')}
                </Button>
              </Grid>
            </Grid>
          </Card>
        ))}
        <Box sx={{flexDirection: 'row'}}>
          <Button size="small" startIcon={<Iconify icon="eva:plus-fill"/>}
                  onClick={() => handleAddGroup(uuidv4())}
                  sx={{flexShrink: 0, mt: 2}}>
            {t('page.recipe.add.form.ingredients.add_new_group')}
          </Button>
        </Box>
        <Typography variant="h3">
          {t('page.recipe.add.form.ingredients.kitchen_appliances')}
        </Typography>
        <Card sx={{p: 3}}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" sx={{color: 'text.disabled', mb: 3}}>
              {t('page.recipe.add.form.ingredients.appliance')}
            </Typography>
            <Stack spacing={2} divider={<Divider flexItem sx={{borderStyle: 'dashed'}}/>}>
              {appliances.map((appliance, applianceIndex) =>
                <Stack spacing={2} key={appliance.id}>
                  <Stack direction="row" spacing={2}>
                    <Controller
                      name={`appliances[${applianceIndex}].selected`}
                      control={control}
                      render={({
                                 field: {onChange, onBlur, value, name, ref},
                                 fieldState: {invalid, isTouched, isDirty, error},
                                 formState
                               }) => (
                        <Autocomplete
                          size="small"
                          label="Name"
                          fullWidth
                          freeSolo
                          openOnFocus
                          autoHighlight
                          autoSelect
                          getOptionDisabled={option => !option.value}
                          onInputChange={(event, newValue) => onEquipmentNameInputChange(event, newValue, applianceIndex)}
                          options={equipmentList[applianceIndex] || []}
                          value={value || null}
                          renderOption={(props, option) => (
                            <li {...props} key={option.value}>
                              {option.label}
                            </li>
                          )}
                          renderInput={(params) =>
                            <TextField
                              {...params}
                              label={t('page.recipe.add.form.ingredients.appliance')}
                              variant="outlined"
                              error={!!error}
                              helperText={error?.message}
                            />
                          }
                          onChange={(event, newValue) => {
                            if (typeof newValue !== 'object') {
                              return;
                            }

                            if (newValue?.addedOption) {
                              newValue.label = newValue.value;
                            }
                            onChange(newValue);
                          }}
                          filterOptions={(options, params) => {
                            const {inputValue} = params;
                            // Suggest the creation of a new value
                            const isExisting = options.some((option) => inputValue === option.title);
                            if (!isExisting) {
                              if (inputValue !== '') {
                                options.push({
                                  value: inputValue,
                                  label: `${t('page.recipe.add.form.ingredients.add_appliance')} "${inputValue}"`,
                                  addedOption: true
                                });
                              } else {
                                options.push({
                                  value: '',
                                  label: t('page.recipe.add.form.ingredients.search_appliance')
                                });
                              }
                            }

                            return options;
                          }}
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                        />
                      )}
                    />
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Iconify icon="eva:trash-2-outline"/>}
                      onClick={() => handleRemoveAppliance(applianceIndex)}
                      sx={{
                        padding: {xs: 0, sm: 'initial'},
                        minWidth: {xs: 0, sm: 'initial'},
                        paddingLeft: {xs: '10px', sm: 'initial'}
                      }}
                    >
                      <Typography variant="button" sx={{display: {xs: 'none', sm: 'inline'}}}>Usuń</Typography>
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Button size="small" startIcon={<Iconify icon="eva:plus-fill"/>}
                    onClick={() => handleAddAppliance()}
                    sx={{flexShrink: 0, mt: 2}}>
              {t('page.recipe.add.form.ingredients.add_new_appliance')}
            </Button>
          </Grid>
        </Card>
        <Box sx={{display: 'flex', justifyContent: 'right'}}>
          <Box sx={{flexDirection: 'row', width: {xs: '100%', sm: 'auto'}}}>
            <LoadingButton type="submit" variant="contained" size="large" loading={isSubmitting}
                           sx={{mt: 2, textAlign: 'right', width: {xs: '100%', sm: 'auto'}}}>
              {t('page.recipe.add.form.ingredients.next_step')}
            </LoadingButton>
          </Box>
        </Box>
      </Stack>
    </Container>
  );
}
